import {
  AdapterConfigurationError,
  DemoJsonMarketDataAdapter,
  parseDemoDataEnvelope,
  parseProductionDataEnvelope,
  type MarketDataAdapter,
} from "../data";
import type { ISODate } from "../domain";
import { DashboardApplicationService } from "./dashboard-service";
import type { ApplicationCatalog } from "./types";

export interface DemoApplicationConfig {
  readonly mode: "demo";
  /** Must be a DemoDataEnvelope; raw payloads and production envelopes are rejected. */
  readonly envelope: unknown;
}

export interface ProductionApplicationConfig {
  readonly mode: "production";
  /** Validated production metadata; it is never replaced with the demo envelope. */
  readonly envelope: unknown;
  readonly adapter?: ProductionMarketDataAdapter;
}

export interface ProductionMarketDataAdapter extends MarketDataAdapter {
  readonly mode: "production";
}

export type DashboardApplicationConfig = DemoApplicationConfig | ProductionApplicationConfig;

function catalogFromEnvelope(envelope: {
  readonly data: ApplicationCatalog;
}): ApplicationCatalog {
  return {
    instruments: envelope.data.instruments,
    pairPresets: envelope.data.pairPresets,
    analysisConfigs: envelope.data.analysisConfigs,
  };
}

export function createDashboardApplication(
  config: DashboardApplicationConfig,
): DashboardApplicationService {
  if (config.mode === "demo") {
    const envelope = parseDemoDataEnvelope(config.envelope);
    return new DashboardApplicationService({
      adapter: new DemoJsonMarketDataAdapter(envelope),
      catalog: catalogFromEnvelope(envelope),
      source: {
        mode: "demo",
        sourceId: envelope.source,
        generatedAt: envelope.generatedAt,
        declaredAsOf: envelope.asOfDate as ISODate,
        synthetic: true,
      },
    });
  }

  if (config.envelope === undefined || config.envelope === null) {
    throw new AdapterConfigurationError(
      "Production metadata envelope is not configured; demo fallback is disabled",
    );
  }
  const envelope = parseProductionDataEnvelope(config.envelope);
  if ("http" in config) {
    throw new AdapterConfigurationError(
      "Production adapter and HTTP configuration cannot be combined; use the server-only production factory",
    );
  }
  if (config.adapter && config.adapter.mode !== "production") {
    throw new AdapterConfigurationError(
      "Production mode requires an adapter with mode='production'; demo fallback is disabled",
    );
  }
  const adapter = config.adapter;
  if (!adapter) {
    throw new AdapterConfigurationError(
      "Production market-data adapter is not configured; demo fallback is disabled",
    );
  }
  return new DashboardApplicationService({
    adapter,
    catalog: catalogFromEnvelope(envelope),
    source: {
      mode: "production",
      sourceId: envelope.source.id,
      generatedAt: envelope.generatedAt,
      declaredAsOf: envelope.asOfDate as ISODate,
      synthetic: false,
    },
  });
}
